import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Truck, Loader2, RefreshCw, CheckCircle2, AlertTriangle, Package, Printer, Download, Clock,
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AmazonFulfillmentActions({ order, onRefresh }) {
  const [shipOpen, setShipOpen] = useState(false);
  const [easyShipOpen, setEasyShipOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const token = localStorage.getItem('token');

  const status = (order.order_status || 'Unshipped').toString();
  const shipped = ['Shipped', 'Delivered', 'Canceled'].includes(status);
  const isEasyShip =
    order.is_easyship === true || order.source_channel_type === 'amazon_in';
  const savedLabel = order.amazon_easyship || null;

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(
        `${API_BASE_URL}/integrations/amazon/orders/${order.id}/refresh`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order refreshed from Amazon');
      if (onRefresh) await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const handlePrintSavedLabel = async () => {
    try {
      const r = await axios.get(
        `${API_BASE_URL}/integrations/amazon/easyship/orders/${order.id}/label`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const url = r.data?.label_data_url;
      if (!url) {
        toast.error('No saved label found');
        return;
      }
      const w = window.open(url, '_blank');
      if (!w) toast.error('Popup blocked — allow popups to print the label');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load label');
    }
  };

  const statusColor = (s) => {
    switch (s) {
      case 'Shipped':
      case 'Delivered':
        return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
      case 'Canceled':
        return 'bg-red-500/15 text-red-300 border-red-500/30';
      case 'Unshipped':
      case 'PartiallyShipped':
        return 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30';
      default:
        return 'bg-wt-subtle text-wt-text-muted border-wt-border';
    }
  };

  return (
    <>
      <div
        className="bg-wt-paper rounded-lg border border-wt-border p-4 space-y-3"
        data-testid="amazon-fulfillment-panel"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Truck className="w-4 h-4 text-[#FF9900]" />
            <span className="text-sm font-medium text-wt-text-primary">
              {order.source_country_label || 'Amazon'} Fulfillment
            </span>
            {isEasyShip && (
              <Badge
                variant="outline"
                className="bg-[#FF9900]/15 text-[#FFAC31] border-[#FF9900]/40 text-[10px] px-1.5 py-0"
                data-testid="amazon-easyship-eligible-badge"
              >
                Easy-Ship eligible
              </Badge>
            )}
          </div>
          <Badge
            variant="outline"
            className={`text-xs ${statusColor(status)}`}
            data-testid="amazon-status-badge"
          >
            {status}
          </Badge>
        </div>

        {order.amazon_ship_submitted_at && (
          <div className="flex items-center gap-2 text-xs text-emerald-300">
            <CheckCircle2 className="w-3 h-3" />
            Shipment submitted {new Date(order.amazon_ship_submitted_at).toLocaleString()}
            {order.awb_number && (
              <span className="text-wt-text-muted">
                • AWB <span className="font-mono text-wt-text-primary">{order.awb_number}</span>
              </span>
            )}
          </div>
        )}

        {savedLabel?.scheduled_package_id && (
          <div
            className="flex items-center justify-between gap-2 p-2 rounded-md bg-[#FF9900]/10 border border-[#FF9900]/30 text-xs text-[#FFAC31]"
            data-testid="amazon-easyship-scheduled-banner"
          >
            <div className="flex items-center gap-2">
              <Package className="w-3.5 h-3.5" />
              Easy-Ship pickup scheduled
              {savedLabel.slot_used?.startTime && (
                <span className="text-wt-text-muted">
                  • slot {new Date(savedLabel.slot_used.startTime).toLocaleString()}
                </span>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs border-[#FF9900]/50 text-[#FFAC31] hover:bg-[#FF9900]/20"
              onClick={handlePrintSavedLabel}
              data-testid="amazon-easyship-reprint-btn"
            >
              <Printer className="w-3 h-3 mr-1" />
              Print label
            </Button>
          </div>
        )}

        {status === 'Canceled' && (
          <div className="flex items-start gap-2 p-2 rounded-md bg-red-500/10 border border-red-500/30 text-xs text-red-200">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Customer canceled — inventory released back to sellable.</span>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {isEasyShip && !shipped && (
            <Button
              size="sm"
              onClick={() => setEasyShipOpen(true)}
              className="h-8 text-xs bg-gradient-to-r from-[#FF9900] to-[#FFAC31] text-black hover:opacity-90"
              data-testid="amazon-easyship-schedule-btn"
            >
              <Package className="w-3.5 h-3.5 mr-1" />
              Schedule Easy-Ship Pickup
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShipOpen(true)}
            disabled={shipped}
            className="border-wt-border text-wt-text-primary hover:bg-wt-subtle h-8 text-xs"
            data-testid="amazon-confirm-shipment-btn"
          >
            <Truck className="w-3.5 h-3.5 mr-1" />
            Confirm Shipment {isEasyShip && '(manual)'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="border-wt-border text-wt-text-primary hover:bg-wt-subtle h-8 text-xs"
            data-testid="amazon-refresh-order-btn"
          >
            {refreshing ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
            )}
            Refresh from Amazon
          </Button>
        </div>
      </div>

      <ConfirmShipmentDialog
        open={shipOpen}
        onOpenChange={setShipOpen}
        order={order}
        token={token}
        onDone={async () => {
          setShipOpen(false);
          if (onRefresh) await onRefresh();
        }}
      />

      <EasyShipDialog
        open={easyShipOpen}
        onOpenChange={setEasyShipOpen}
        order={order}
        token={token}
        onDone={async () => {
          setEasyShipOpen(false);
          if (onRefresh) await onRefresh();
        }}
      />
    </>
  );
}

function ConfirmShipmentDialog({ open, onOpenChange, order, token, onDone }) {
  const [form, setForm] = useState({
    carrier_code: order.carrier_code || 'Delhivery',
    tracking_number: order.awb_number || '',
    shipping_method: 'Standard',
  });
  const [submitting, setSubmitting] = useState(false);
  const [carriers, setCarriers] = useState([
    'Delhivery', 'Bluedart', 'DTDC', 'Ecom Express', 'XpressBees',
    'Shadowfax', 'India Post', 'DHL', 'FedEx', 'UPS', 'Aramex', 'Amazon Shipping',
  ]);

  useEffect(() => {
    if (!open) return;
    axios
      .get(`${API_BASE_URL}/integrations/amazon/carriers`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        if (Array.isArray(r.data?.carriers) && r.data.carriers.length) {
          setCarriers(r.data.carriers);
        }
      })
      .catch(() => { /* fall back to the seeded list */ });
  }, [open, token]);

  const submit = async () => {
    if (!form.carrier_code || !form.tracking_number) {
      toast.error('Carrier and tracking number are required');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API_BASE_URL}/integrations/amazon/orders/${order.id}/confirm-shipment`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(r.data?.message || 'Shipment confirmed on Amazon');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Confirm shipment failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="amazon-confirm-shipment-dialog">
        <DialogHeader>
          <DialogTitle>Confirm shipment on Amazon</DialogTitle>
          <DialogDescription className="text-xs">
            Sends a <code>POST_ORDER_FULFILLMENT_DATA</code> feed to Amazon. The buyer will
            be notified with tracking and the return window will start.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Carrier (must match Amazon's list for VTR)</Label>
            <Input
              list="amazon-carriers"
              value={form.carrier_code}
              onChange={(e) => setForm({ ...form, carrier_code: e.target.value })}
              placeholder="e.g. Delhivery"
              data-testid="amazon-ship-carrier"
            />
            <datalist id="amazon-carriers">
              {carriers.map((c) => <option key={c} value={c} />)}
            </datalist>
            <p className="text-[10px] text-wt-text-muted mt-1">
              Exact-matching a name from Amazon's accepted list protects your
              Valid Tracking Rate. Unknown names default to "Other" (VTR won't count).
            </p>
          </div>
          <div>
            <Label className="text-xs">Tracking number (AWB)</Label>
            <Input
              value={form.tracking_number}
              onChange={(e) => setForm({ ...form, tracking_number: e.target.value })}
              placeholder="AWB1234567890"
              data-testid="amazon-ship-awb"
            />
          </div>
          <div>
            <Label className="text-xs">Shipping method (optional)</Label>
            <Input
              value={form.shipping_method}
              onChange={(e) => setForm({ ...form, shipping_method: e.target.value })}
              placeholder="Standard"
              data-testid="amazon-ship-method"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting} data-testid="amazon-ship-submit">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Truck className="w-4 h-4 mr-2" />}
            Submit to Amazon
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Easy-Ship dialog — slot picker + package selector → schedule pickup
// ---------------------------------------------------------------------------
function EasyShipDialog({ open, onOpenChange, order, token, onDone }) {
  const accountId = order.channel_account_id || order.source_channel_id;
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [preference, setPreference] = useState(null);
  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [resolvedPackage, setResolvedPackage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadSlotsAndPref = async () => {
    if (!accountId || !order.amazon_order_id) return;
    setLoadingSlots(true);
    try {
      const [slotsRes, prefRes, pkgsRes, resolvedRes] = await Promise.all([
        axios.get(
          `${API_BASE_URL}/integrations/amazon/easyship/accounts/${accountId}/timeslots`,
          {
            params: { amazon_order_id: order.amazon_order_id },
            headers: { Authorization: `Bearer ${token}` },
          }
        ).catch((e) => ({ error: e })),
        axios.get(
          `${API_BASE_URL}/integrations/amazon/easyship/accounts/${accountId}/preference`,
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => ({ data: { preference: null } })),
        axios.get(`${API_BASE_URL}/couriers/packages`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: { packages: [] } })),
        axios.get(
          `${API_BASE_URL}/integrations/amazon/easyship/resolved-package`,
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => ({ data: null })),
      ]);
      if (slotsRes.error) {
        const detail = slotsRes.error.response?.data?.detail || '';
        if (detail) toast.error(detail);
        setSlots([]);
      } else {
        const fetched = slotsRes.data?.slots || [];
        setSlots(fetched);
        if (fetched.length && !selectedSlot) setSelectedSlot(fetched[0]);
      }
      setPreference(prefRes.data?.preference || null);
      setPackages(pkgsRes.data?.packages || []);
      setResolvedPackage(resolvedRes.data || null);
      // Auto-pick the default package in the dropdown if any.
      const def = (pkgsRes.data?.packages || []).find((p) => p.is_default);
      if (def) setSelectedPackageId(def.id);
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    if (open) loadSlotsAndPref();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Refresh the 'Using: X' banner whenever user picks a different package.
  useEffect(() => {
    if (!open || !selectedPackageId) return;
    axios
      .get(
        `${API_BASE_URL}/integrations/amazon/easyship/resolved-package`,
        {
          params: { package_id: selectedPackageId },
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((r) => setResolvedPackage(r.data))
      .catch(() => {});
  }, [selectedPackageId, open, token]);

  const usePreference = () => {
    if (!preference?.slot_start_time) return;
    setSelectedSlot({
      slot_id: preference.slot_id,
      start_time: preference.slot_start_time,
      end_time: preference.slot_end_time,
    });
    toast.success('Using saved default slot');
  };

  const submit = async () => {
    if (!selectedSlot && !preference?.slot_start_time) {
      toast.error('Pick a pickup slot (or save a default first)');
      return;
    }
    if (!packages.length) {
      toast.error('No packages defined — open Inventory → Packages to add one.');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        slot_id: selectedSlot?.slot_id,
        slot_start_time: selectedSlot?.start_time,
        slot_end_time: selectedSlot?.end_time,
        package_id: selectedPackageId || undefined,
      };
      const r = await axios.post(
        `${API_BASE_URL}/integrations/amazon/easyship/accounts/${accountId}/orders/${order.id}/schedule-package`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(r.data?.message || 'Easy-Ship pickup scheduled');
      if (r.data?.label_data_url) {
        const w = window.open(r.data.label_data_url, '_blank');
        if (!w) toast.message('Label ready — popup blocked. Use "Print label" to view.');
      }
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to schedule Easy-Ship pickup');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="amazon-easyship-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-[#FF9900]" />
            Schedule Amazon Easy-Ship pickup
          </DialogTitle>
          <DialogDescription className="text-xs">
            Amazon will pick up the package from your warehouse and generate a
            native Amazon Shipping label that counts toward Valid Tracking Rate.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {/* Saved preference banner */}
          {preference?.slot_start_time && (
            <div
              className="flex items-center justify-between gap-2 p-2 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-200"
              data-testid="amazon-easyship-preference-banner"
            >
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" />
                <span>
                  Default slot saved: <strong>{preference.description}</strong>
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-6 text-[10px] border-emerald-500/40 text-emerald-200"
                onClick={usePreference}
                data-testid="amazon-easyship-use-default-slot"
              >
                Use default
              </Button>
            </div>
          )}

          {/* Slot picker */}
          <div>
            <Label className="text-xs flex items-center justify-between">
              <span>Pickup slot</span>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-[10px]"
                onClick={loadSlotsAndPref}
                disabled={loadingSlots}
              >
                {loadingSlots ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="w-3 h-3 mr-1" />
                )}
                Refresh
              </Button>
            </Label>
            <div
              className="mt-1 max-h-44 overflow-y-auto rounded border border-wt-border divide-y divide-wt-border"
              data-testid="amazon-easyship-slots"
            >
              {loadingSlots && (
                <div className="p-3 text-xs text-wt-text-muted flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Loading slots from Amazon…
                </div>
              )}
              {!loadingSlots && slots.length === 0 && (
                <div className="p-3 text-xs text-wt-text-muted">
                  No Easy-Ship slots returned. Either this order is not Easy-Ship
                  eligible, or Amazon has no open slots right now. Use "Confirm
                  Shipment (manual)" as a fallback.
                </div>
              )}
              {slots.map((s, i) => {
                const isSelected =
                  selectedSlot &&
                  selectedSlot.start_time === s.start_time &&
                  selectedSlot.end_time === s.end_time;
                return (
                  <button
                    key={`${s.start_time}-${i}`}
                    type="button"
                    onClick={() => setSelectedSlot(s)}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between transition-colors ${
                      isSelected
                        ? 'bg-[#FF9900]/15 text-[#FFAC31]'
                        : 'hover:bg-wt-subtle text-wt-text-primary'
                    }`}
                    data-testid={`amazon-easyship-slot-${i}`}
                  >
                    <span>
                      {new Date(s.start_time).toLocaleString()} →{' '}
                      {new Date(s.end_time).toLocaleString()}
                    </span>
                    <span className="text-wt-text-muted text-[10px]">
                      {s.handover_method || 'PICKUP'}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Package picker (pulls from /couriers/packages aka /inventory/packages) */}
          <div>
            <Label className="text-xs mb-1 block flex items-center justify-between">
              <span>Package</span>
              <a
                href="/inventory/packages"
                target="_blank"
                rel="noreferrer"
                className="text-[10px] text-wt-text-muted hover:text-wt-gold underline"
              >
                Manage packages ↗
              </a>
            </Label>
            {packages.length === 0 ? (
              <div
                className="p-3 rounded border border-amber-500/40 bg-amber-500/10 text-xs text-amber-200"
                data-testid="amazon-easyship-no-packages-banner"
              >
                No packages defined yet. Head to{' '}
                <a
                  href="/inventory/packages"
                  target="_blank"
                  rel="noreferrer"
                  className="underline font-medium"
                >
                  Inventory → Packages
                </a>{' '}
                to add one (length, width, height, weight). Easy-Ship requires
                declared dimensions for every shipment.
              </div>
            ) : (
              <select
                value={selectedPackageId}
                onChange={(e) => setSelectedPackageId(e.target.value)}
                className="w-full text-xs bg-wt-bg border border-wt-border rounded px-3 py-2 focus:outline-none focus:border-wt-gold"
                data-testid="amazon-easyship-package-select"
              >
                {packages.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.length}×{p.width}×{p.height}
                    {p.dimension_unit} · {p.weight}
                    {p.weight_unit}){p.is_default ? ' — default' : ''}
                  </option>
                ))}
              </select>
            )}
            {resolvedPackage && (
              <p className="text-[10px] text-wt-text-muted mt-1" data-testid="amazon-easyship-resolved-hint">
                Using:{' '}
                <span className="text-wt-text-primary">
                  {resolvedPackage.package_name || 'fallback defaults'}
                </span>{' '}
                · {resolvedPackage.dimensions.length}×{resolvedPackage.dimensions.width}×
                {resolvedPackage.dimensions.height} {resolvedPackage.dimensions.unit.toLowerCase()} /{' '}
                {resolvedPackage.weight.value} {resolvedPackage.weight.unit.toLowerCase()}
                {resolvedPackage.source === 'fallback' && (
                  <span className="text-amber-300">
                    {' '}
                    — fallback (no default package saved). Set one in Inventory → Packages to avoid Amazon weight penalties.
                  </span>
                )}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !selectedSlot || packages.length === 0}
            className="bg-gradient-to-r from-[#FF9900] to-[#FFAC31] text-black hover:opacity-90"
            data-testid="amazon-easyship-submit"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Schedule &amp; generate label
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
